import { useEffect, useState } from "react";
import { Toast, ToastViewport, type ToastType } from "@v0y4ge/jrni-ui";

/**
 * Imperative toast dispatcher for the ESM app — the real replacement for the
 * prototype's `window.toast`. A module-level store + subscription (same pattern
 * as access.ts); `<ToastHost/>` (mounted once in App) renders the stack.
 *
 *   import { toast } from "../shell/toast";
 *   toast({ tone: "success", title: "Saved", subtitle: "Your changes are stored." });
 */
export interface ToastInput {
  /** Severity — prototype name. Mapped to the jrni-ui Toast `type`. */
  tone?: ToastType;
  title: string;
  /** Prototype name for the body line. */
  subtitle?: string;
  description?: string;
  /** Prototype alias for the body line — same slot as `subtitle`/`description`.
   * Accepted because call sites reach for it naturally (Orders collect/refund);
   * one of the three wins, first non-empty. */
  message?: string;
  autoDismissMs?: number;
}
interface ToastEntry extends ToastInput { id: number; }

let _toasts: ToastEntry[] = [];
let _seq = 1;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });

export function toast(input: ToastInput): number {
  const id = _seq++;
  _toasts = [..._toasts, { ...input, id }];
  _emit();
  return id;
}
export function dismissToast(id: number) {
  _toasts = _toasts.filter((t) => t.id !== id);
  _emit();
}

export function ToastHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    _subs.add(f);
    return () => { _subs.delete(f); };
  }, []);
  if (_toasts.length === 0) return null;
  return (
    <ToastViewport placement="bottom-right">
      {_toasts.map((t) => (
        <Toast
          key={t.id}
          type={t.tone || "info"}
          layout="notification"
          title={t.title}
          description={t.subtitle || t.description || t.message}
          dismissible
          autoDismissMs={t.autoDismissMs ?? 4500}
          onDismiss={() => dismissToast(t.id)}
        />
      ))}
    </ToastViewport>
  );
}
